import { api, withFreshAccess } from './client'
import { nextAccount } from './accountPool'
import { db } from '../cache/db'

const BATCH = 25

/**
 * Non-destructive deletion check for db.posts.
 *
 * Reads every cached post URI (sorted for stable resume ordering), batches
 * them through app.bsky.feed.getPosts, and reports which ones Bluesky no
 * longer returns (deleted / private / gone).
 * NEVER writes to or removes from any cache table.
 *
 * opts.startFromOffset — skip the first N URIs (resume an interrupted run).
 * opts.onBatch        — called after each batch with newly-found missing URIs,
 *                       the total checked so far, and the grand total. Use this
 *                       for streaming / progressive UI updates.
 * opts.onProgress     — legacy progress-only callback (checked, total).
 */
export async function checkPostsForDeletion(
  onProgressOrOpts?:
    | ((checked: number, total: number) => void)
    | {
        startFromOffset?: number
        /** When set, only scan posts belonging to this DID (profile-scoped check). */
        filterDid?: string
        /** Explicit URI list to check — bypasses db.posts scan entirely. */
        uris?: string[]
        signal?: AbortSignal
        onBatch?: (newMissing: string[], checked: number, total: number) => void
        onProgress?: (checked: number, total: number) => void
      },
): Promise<{ missing: string[]; checked: number; total: number }> {
  const acc = nextAccount()
  if (!acc) return { missing: [], checked: 0, total: 0 }

  // Normalise the overloaded first argument.
  const opts =
    typeof onProgressOrOpts === 'function'
      ? { onProgress: onProgressOrOpts }
      : (onProgressOrOpts ?? {})

  let allUris: string[]
  if (opts.uris) {
    // Explicit URI list — sort for stable ordering.
    allUris = [...opts.uris].sort()
  } else {
    const rows = opts.filterDid
      ? await db.posts.where('did').equals(opts.filterDid).toArray()
      : await db.posts.toArray()
    // Sort by URI for a stable ordering that makes startFromOffset reliable
    // across sessions (new posts inserted at the end when sorted).
    allUris = rows.map((r) => r.uri).sort()
  }
  const total = allUris.length
  if (total === 0) return { missing: [], checked: 0, total: 0 }

  const startOffset = opts.startFromOffset ?? 0
  const uris = startOffset > 0 ? allUris.slice(startOffset) : allUris
  const missing: string[] = []
  let checked = startOffset

  for (let i = 0; i < uris.length; i += BATCH) {
    if (opts.signal?.aborted) break
    const batch = uris.slice(i, i + BATCH)
    const batchMissing: string[] = []
    try {
      const qs = batch.map((u) => `uris=${encodeURIComponent(u)}`).join('&')
      const resp = await withFreshAccess(acc.id, () =>
        api.xrpc<{ posts?: { uri: string }[] }>({
          accountId: acc.id,
          method: 'GET',
          path: `app.bsky.feed.getPosts?${qs}`,
        }),
      )
      const returned = new Set((resp.posts ?? []).map((p) => p.uri))
      for (const u of batch) {
        if (!returned.has(u)) batchMissing.push(u)
      }
    } catch {
      // Skip batch on error — network blip shouldn't flag everything as missing.
    }
    missing.push(...batchMissing)
    checked += batch.length
    if (batchMissing.length > 0) {
      opts.onBatch?.(batchMissing, checked, total)
    }
    opts.onProgress?.(checked, total)
  }

  return { missing, checked, total }
}
